import { useUserStore } from '@/stores/userStore'

const getAnonymousId = () => {
    const STORAGE_KEY = 'mp_anonymous_id';
    let id = localStorage.getItem(STORAGE_KEY);
    
    if (!id) {
      id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
      localStorage.setItem(STORAGE_KEY, id);
    }
    
    return id;
  };
// Base configuration
const BASE_URL = 'https://api.news.miracleplus.com';

// Request headers configuration
const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

// Common response handler
const handleResponse = async (response) => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  // API returns { message: string, request_id: string, success: boolean }
  // success=false only indicates the result content, not request failure
  return data;
};

// Request wrapper
const request = async (endpoint, options = {}) => {
  try {
    const { headers = {}, requireAuth = false, ...restOptions } = options;
    
    // 获取用户store
    const userStore = useUserStore();
    
    // 构建请求头
    const finalHeaders = {
      ...DEFAULT_HEADERS,
      'Anonymous-Id': getAnonymousId(),
      ...headers,
    };

    // 如果需要认证或用户已登录，添加认证信息
    if (requireAuth) {
      if (!userStore.isLoggedIn) {
        console.log('This API requires authentication');
        window.webf.hybridHistory.pushState({}, '/login');
        return;
      }
    }
    finalHeaders['Authorization'] = `Bearer ${userStore.userInfo?.token || undefined}`;


    // 处理请求体
    if (restOptions.body) {
      const bodyObj = JSON.parse(restOptions.body);
      bodyObj.anonymous_id = bodyObj.anonymous_id || getAnonymousId();
      restOptions.body = JSON.stringify(bodyObj);
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      headers: finalHeaders,
      ...restOptions,
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// API endpoints
export const api = {
  // Auth APIs
  auth: {
    loginByPhonePassword: (data) => request('/v1/users/login_by_phone_password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    loginByPhoneCode: (data) => request('/v1/users/login_by_phone_code', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    register: (data) => request('/v1/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    getUserInfo: () => request('/v1/users/user_info', {
      requireAuth: true,
    }),
    updateUserInfo: (id, data) => request(`/v1/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      requireAuth: true,
    }),
    sendVerifyCode: ({ countryCode, phone, useCase }) => request('/v1/users/sms_send', {
      method: 'POST',
      body: JSON.stringify({ country_code: countryCode, phone, use_case: useCase }),
    }),
    logout: () => request('/v1/users/user_info', {
      requireAuth: false,
    }),
    resetPassword: ({ code, countryCode, phone, newPassword }) => request('/v1/users/reset_password', {
      method: 'POST',
      body: JSON.stringify({ code, country_code: countryCode, phone, new_password: newPassword }),
    }),
    getUserFeedsList: ({ page = 1, category = 'all', userId = '' } = {}) => 
      request(`/v1/users/${userId}/feeds?page=${page}&category=${category}`, {
        requireAuth: true,
    }),
    getUserNotifications: ({ page = 1 } = {}) => 
      request(`/v1/notifications?page=${page}`, {
        requireAuth: true,
    }),
  },

  // News APIs
  news: {
    getHotList: ({ page = 1 } = {}) => request(`/v1/feeds/hot?page=${page}`),
    getLatestList: ({ page = 1 } = {}) => request(`/v1/feeds/latest?page=${page}`),
    getCommentList: ({ page = 1 } = {}) => request(`/v1/feeds/comment?page=${page}`),
    getDisplayList: ({ page = 1, topic = '' } = {}) => 
      request(`/v1/displays?page=${page}&topic=${topic}`),
    getDetail: (id) => request(`/v1/share_links/${id}`),
    getNotes: (id) => request(`/v1/share_links/${id}/notes`),
    getRecommendations: (id) => request(`/v1/share_links/${id}/recommend`),
    publish: (data) => request('/v1/share_links', {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: true,
    }),
    viewCount: ({ id }) => request(`/v1/view_counts`, { 
      method: 'POST',
      body: JSON.stringify({
        id,
        model_type: 'ShareLink',
      }),
    }),
    follow: (id) => request(`/v1/follow_items`, {
      method: 'POST',
      body: JSON.stringify({
        followables: [
          {
            followCheck: true,
            followableId: id,
            followableType: 'ShareLink',
          }
        ]
      }),
      requireAuth: true,
    }),
    unfollow: (id) => request(`/v1/follow_items`, {
      method: 'POST',
      body: JSON.stringify({
        followables: [
          {
            followCheck: false,
            followableId: id,
            followableType: 'ShareLink',
          }
        ]
      }),
      requireAuth: true,
    }),
    like: (id) => request(`/v1/likes/like`, {
      method: 'POST',
      body: JSON.stringify({
        resource_id: id,
        resource_type: 'ShareLink',
      }),
    }),
    unlike: (id) => request(`/v1/likes/neutral`, {
      method: 'POST',
      body: JSON.stringify({
        resource_id: id,
        resource_type: 'ShareLink',
      }),
    }),
    bookmark: (id) => request(`/v1/bookmarks`, {
      method: 'POST',
      body: JSON.stringify({
        resource_id: id,
        resource_type: 'ShareLink',
      }),
    }),
  },

  // Comment APIs
  comments: {
    getShareLinkComments: ({ page = 1, limit = 20, id = '' }) => request(`/v1/comments?resource_id=${id}&limit=${limit}&page=${page}&resource_type=ShareLink`),
    getSingleComment: (id = '') => request(`/v1/comments/${id}`),
    getSubComments: (id = '') => request(`/v1/comments?resource_id=${id}&resource_type=Comment`),
    getList: ({ page = 1, resourceId = '', resourceType = 'ShareLink'} = {}) => 
      request(`/v1/comments?page=${page}&resource_id=${resourceId}&resource_type=${resourceType}`),
    // 评论帖子传入 content
    // 评论评论传入 richContent
    // TODO: rootId 传入时机
    create: ({ resourceId, resourceType, content, richContent, rootId }) => request('/v1/comments', {
      method: 'POST',
      body: JSON.stringify({
        resource_id: resourceId,
        resource_type: resourceType,
        content,
        rich_content: richContent,
        root_id: rootId,
      }),
      requireAuth: true,
    }),
    update: ({ id, content, richContent }) => request(`/v1/comments/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        content,
        rich_content: richContent,
      }),
      requireAuth: true,
    }),
    reply: ({ id, richContent, rootId }) => {
      return request(`/v1/comments`, {
        method: 'POST',
        body: JSON.stringify({
          resource_id: id,
          resource_type: 'Comment',
          rich_content: richContent,
          ...(rootId && { root_id: rootId }), // Only include root_id if it exists
        }),
        requireAuth: true,
      });
    },
    createByNote: ({ noteId }) => request(`/v1/notes/${noteId}/comment`, {
      method: 'POST',
      body: JSON.stringify({}),
      requireAuth: true,
    }),
    like: (id) => request(`/v1/likes/like`, {
      method: 'POST',
      body: JSON.stringify({
        resource_id: id,
        resource_type: 'Comment',
      }),
    }),
    unlike: (id) => request(`/v1/likes/neutral`, {
      method: 'POST',
      body: JSON.stringify({
        resource_id: id,
        resource_type: 'Comment',
      }),
    }),
  },

  // User APIs
  user: {
    getFeeds: ({ page = 1, category = 'all', userId = '' } = {}) => request(`/v1/users/${userId}/feeds?page=${page}&category=${category}`, {
        requireAuth: true,
    }),
    getInvitedUsers: ({ resource, id, search = '' } = {}) => request(`/v1/users/invited_users?resource=${resource}&id=${id}&search=${search}`, {
      requireAuth: true,
    }),
    invite: ({ resourceType, resourceId, userId } = {}) => request('/v1/users/invite_user', {
      method: 'POST',
      body: JSON.stringify({
        resource_type: resourceType,
        resource_id: resourceId,
        user_id: userId,
      }),
      requireAuth: true,
    }),
  },
  // Search APIs
  search: {
    users: ({ keyword = '', perPage = 10 } = {}) => request(`/v1/users/search?keyword=${keyword}&per_page=${perPage}`, {
        requireAuth: true,
    }),
    answers: ({ keyword = '', perPage = 10 } = {}) => request(`/v1/answers/search?keyword=${keyword}&per_page=${perPage}`, {
        requireAuth: true,
    }),
    questions: ({ keyword = '', perPage = 10 } = {}) => request(`/v1/questions/search?keyword=${keyword}&per_page=${perPage}`, {
        requireAuth: true,
    }),
    shareLinks: ({ keyword = '', perPage = 10 } = {}) => request(`/v1/share_links/search?keyword=${keyword}&per_page=${perPage}`, {
        requireAuth: true,
    }),
  },
  question: {
    getDetail: (id) => request(`/v1/questions/${id}`, { 
        requireAuth: true,
    }),
    getAnswerDetail: (id) => request(`/v1/answers/${id}`, {
      requireAuth: true,
    }),
    follow: (id) => request(`/v1/follow_items`, {
      method: 'POST',
      body: JSON.stringify({
        followables: [
          {
            followCheck: true,
            followableId: id,
            followableType: 'Question',
          }
        ]
      }),
      requireAuth: true,
    }),
    unfollow: (id) => request(`/v1/follow_items`, {
      method: 'POST',
      body: JSON.stringify({
        followables: [
          {
            followCheck: false,
            followableId: id,
            followableType: 'Question',
          }
        ]
      }),
      requireAuth: true,
    }),
    viewCount: ({ id }) => request(`/v1/view_counts`, { 
      method: 'POST',
      body: JSON.stringify({
        id,
        model_type: 'Question',
      }),
    }),
    answer: ({ questionId, content }) => request(`/v1/answers`, {
      method: 'POST',
      body: JSON.stringify({
        content_type: 'slatejs',
        content,
        question_id: questionId,
      }),
    }),
  },
  topic: {
    getDetail: (id) => request(`/v1/topics/${id}`, {
      requireAuth: true,
    }),
  }
};
